import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { unscopedKey } from "@/lib/queryClient";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import type { Match } from "@/lib/types";
import { useChunkedUpload, type ResumableSession } from "./useChunkedUpload";
import { TkticSelect } from "@/components/ui/TkticSelect";
import {
  defaultSlotForMatch,
  SLOT_LABELS,
  slotsForMatch,
  type MatchSlot,
} from "@/features/matches/matchSlots";

/**
 * CoachMatchUploadModal — coach-facing upload form for a match slot (M12b).
 *
 * Uses the chunked upload-session API
 * (`POST /api/matches/{id}/upload-video/session` → `PUT /api/uploads/sessions/{id}/chunk × N`
 * → `POST /api/uploads/sessions/{id}/complete`) so 4–10 GB 4K match
 * videos can be uploaded through Cloudflare's 100 MB body limit. Each
 * chunk is the server's configured `chunk_size` (default 16 MB).
 *
 * Cross-reload resume (PR #90):
 * - On session create the hook persists {session_id, file_name, file_size}
 *   to localStorage. If the user reloads / closes the tab mid-upload and
 *   later re-picks the same file, the modal calls `discoverActiveSession`
 *   and offers to resume from the server's `next_index`.
 *
 * Destructive-cancel confirm (PR #90):
 * - Cancelling mid-upload deletes the partial raw file server-side. For
 *   uploads with >2 chunks already on the server, we show a ConfirmDialog
 *   so a misclicked Cancel / Close / Escape / backdrop can't wipe out
 *   meaningful work. Pre-upload phases and "first few chunks" cancels
 *   skip the confirm because they're cheap to redo.
 *
 * On success the modal invalidates every matches cache (so the row's
 * "ready / transcoding / error" counts update without a manual refresh),
 * then closes.
 */

/** Above this many already-uploaded chunks, the Cancel/Close path shows a
 * confirm dialog. Threshold picked so a misclick during the first ~32 MB
 * (small file or just-started upload) doesn't slow the user down, while
 * a long-running multi-GB upload is protected against accidental cancel. */
const CONFIRM_AFTER_CHUNKS = 2;

export function CoachMatchUploadModal({
  match,
  onClose,
}: {
  match: Match;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  // Slot options follow the match's video layout — a `two_halves`
  // match has no `full` slot, a `full` match has only `full`. The
  // state seeds to the match's first available slot.
  const slotOptions = slotsForMatch(match);
  const [slot, setSlot] = useState<MatchSlot>(() =>
    defaultSlotForMatch(match),
  );
  const [file, setFile] = useState<File | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [resumeOffer, setResumeOffer] = useState<ResumableSession | null>(null);
  const [confirmCancelOpen, setConfirmCancelOpen] = useState(false);
  const {
    state: uploadState,
    start,
    cancel,
    discoverActiveSession,
    clearStoredSessionFor,
  } = useChunkedUpload();

  const pending =
    uploadState.phase === "hashing"
    || uploadState.phase === "creating-session"
    || uploadState.phase === "uploading"
    || uploadState.phase === "completing";

  const error = formError ?? uploadState.error;

  const percent =
    uploadState.totalBytes > 0
      ? Math.min(
          100,
          Math.round(
            (uploadState.bytesUploaded / uploadState.totalBytes) * 100,
          ),
        )
      : null;

  // Whether the current pending state has enough work invested that a
  // cancel should ask for confirmation. Pre-upload phases and the first
  // few chunks are cheap to redo; everything beyond that warrants a prompt.
  const cancelWouldDiscardWork =
    uploadState.phase === "uploading"
    && uploadState.currentChunkIndex >= CONFIRM_AFTER_CHUNKS;

  useEffect(() => {
    previousFocusRef.current =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;
    inputRef.current?.focus();
    const previousFocus = previousFocusRef.current;
    return () => {
      previousFocus?.focus();
    };
  }, []);

  // Close the modal + invalidate caches when the chunked upload reports
  // `done`. Done is a terminal state from the hook's POV — backend has
  // already enqueued the transcode and will surface progress via the
  // match row's video_status counts.
  useEffect(() => {
    if (uploadState.phase !== "done") return;
    let cancelled = false;
    (async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: unscopedKey("public-matches"),
        }),
        queryClient.invalidateQueries({
          queryKey: unscopedKey("admin-matches"),
        }),
        queryClient.invalidateQueries({ queryKey: ["admin-matches-scoped"] }),
        queryClient.invalidateQueries({ queryKey: ["coach-matches"] }),
      ]);
      if (!cancelled) onClose();
    })();
    return () => {
      cancelled = true;
    };
  }, [uploadState.phase, queryClient, onClose]);

  // When the user picks a file, check whether an interrupted upload exists
  // for this {match, slot} that matches the picked file. If so, offer to
  // resume. Re-runs when slot changes too — same picked file in a different
  // slot has no resume context.
  useEffect(() => {
    if (!file || pending) {
      setResumeOffer(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const offer = await discoverActiveSession(file, match.id, slot);
        if (!cancelled) setResumeOffer(offer);
      } catch {
        // Discovery failures are non-fatal — the user just doesn't get a
        // resume prompt. They can still start a fresh upload normally.
        if (!cancelled) setResumeOffer(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [file, match.id, slot, pending, discoverActiveSession]);

  const performCancelAndClose = () => {
    cancel();
    onClose();
  };

  const cancelAndClose = () => {
    // Once we've handed off to `POST /complete` the upload has effectively
    // succeeded — the server is just spawning transcode. Cancelling now
    // would race the DELETE against a session that's already marked
    // completed (see useChunkedUpload.cancel's guard). Just close the
    // modal; the `phase === "done"` effect handles cache invalidation
    // when the complete returns.
    if (uploadState.phase === "completing") {
      onClose();
      return;
    }
    // Confirm before discarding meaningful upload progress. Pre-upload
    // states and "first couple chunks" cancels skip the confirm because
    // a misclick is cheap to redo.
    if (cancelWouldDiscardWork) {
      setConfirmCancelOpen(true);
      return;
    }
    performCancelAndClose();
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Escape") {
      // If the confirm dialog is open, let IT handle Escape (it uses
      // the same useOneShotModal Escape primitive). The modal's Escape
      // handler must not double-fire when a child confirm is up.
      if (confirmCancelOpen) return;
      event.preventDefault();
      cancelAndClose();
    }
  };

  const onSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!file) {
      setFormError("Pick a video file (.mp4 or .mkv) first.");
      return;
    }
    setFormError(null);
    // Fire-and-forget — the hook's state machine drives the UI. The
    // success-side effect above handles cache invalidation + close.
    // If the user picked Resume on the offer prompt, pass the ResumableSession
    // through so `start` skips POST /session and jumps into the chunk loop.
    void start(file, {
      matchId: match.id,
      slot,
      ...(resumeOffer ? { resumeFrom: resumeOffer } : {}),
    }).catch(() => {
      /* hook already set error state */
    });
  };

  const phaseLabel = (() => {
    switch (uploadState.phase) {
      case "hashing":
        return "Preparing…";
      case "creating-session":
        return "Starting upload…";
      case "uploading":
        if (uploadState.totalChunks > 1) {
          return `Uploading chunk ${uploadState.currentChunkIndex + 1} of ${uploadState.totalChunks}…`;
        }
        return "Uploading…";
      case "completing":
        return "Finalizing…";
      default:
        return null;
    }
  })();

  const cancelConfirmMessage = (() => {
    const uploadedMb = (uploadState.bytesUploaded / 1024 / 1024).toFixed(0);
    const totalMb = (uploadState.totalBytes / 1024 / 1024).toFixed(0);
    return (
      `You've uploaded ${uploadState.currentChunkIndex} of ${uploadState.totalChunks} chunks `
      + `(${uploadedMb} of ${totalMb} MB). `
      + `Cancelling will discard this progress and delete the partial file from the server.`
    );
  })();

  return (
    <div
      ref={dialogRef}
      role="dialog"
      aria-modal="true"
      aria-label="Upload match video"
      data-testid="coach-upload-modal"
      onKeyDown={handleKeyDown}
      onClick={(e) => {
        // Backdrop click: only if the confirm dialog isn't on top of us.
        // The confirm portals to document.body and traps clicks there, so
        // a click inside the confirm won't bubble here. But a click on
        // OUR backdrop while the confirm is open should also be a no-op
        // (otherwise dismissing the confirm via its backdrop would also
        // fire the modal's backdrop → double-action footgun).
        if (confirmCancelOpen) return;
        if (e.target === e.currentTarget) cancelAndClose();
      }}
      className="fixed inset-0 z-[140] flex items-end justify-center bg-black/80 p-3 sm:items-center sm:p-4"
    >
      <form
        onSubmit={onSubmit}
        className="flex max-h-[calc(100dvh-1.5rem)] w-full max-w-md flex-col gap-4 overflow-y-auto rounded-xl border border-rule bg-surface p-4 pb-[calc(1rem+env(safe-area-inset-bottom))] shadow-2xl sm:p-5"
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h3 className="text-base font-display uppercase tracking-wider text-ink">
              Upload match video
            </h3>
            <p className="mt-1 text-xs text-ink-muted truncate">
              {match.home_team || "—"} vs {match.away_team || "—"}
              {match.date ? ` · ${match.date}` : ""}
            </p>
          </div>
          <button
            type="button"
            onClick={cancelAndClose}
            data-testid="coach-upload-modal-close"
            className="rounded border border-rule px-2 py-1 text-xs text-ink-muted hover:text-ink"
          >
            Close
          </button>
        </div>

        <label className="flex flex-col gap-1 text-xs text-ink-muted">
          <span className="font-display uppercase tracking-wide">Slot</span>
          <TkticSelect
            value={slot}
            onChange={(next) => setSlot(next as MatchSlot)}
            disabled={pending}
            data-testid="coach-upload-modal-slot"
            ariaLabel="Slot"
            className="w-full"
            options={slotOptions.map((value) => ({
              value,
              label: SLOT_LABELS[value],
            }))}
          />
        </label>

        <label className="flex flex-col gap-1 text-xs text-ink-muted">
          <span className="font-display uppercase tracking-wide">
            Video file (.mp4 or .mkv)
          </span>
          {/* Native file pickers can't be replaced entirely (browser
            * security forbids triggering them from synthetic events),
            * but the `file:` Tailwind variants restyle the inner button
            * into a brand accent pill — same pattern used by
            * `MatchEditModal`'s opponent-logo picker. Mirrors the
            * no-native-browser-chrome rule for everything we can style. */}
          <input
            ref={inputRef}
            type="file"
            accept=".mp4,.mkv,video/mp4,video/x-matroska"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            disabled={pending}
            data-testid="coach-upload-modal-file"
            className="block w-full text-xs text-ink file:mr-2 file:rounded file:border-0 file:bg-accent/15 file:px-2 file:py-1 file:text-[0.65rem] file:font-display file:uppercase file:tracking-wider file:text-accent hover:file:bg-accent/25 disabled:opacity-60 disabled:file:cursor-not-allowed"
          />
          {file ? (
            <span className="mt-1 font-mono text-[0.6rem] text-ink-muted">
              {(file.size / 1024 / 1024).toFixed(1)} MB
            </span>
          ) : null}
        </label>

        {resumeOffer && !pending ? (
          <div
            data-testid="coach-upload-modal-resume-offer"
            className="flex flex-col gap-2 rounded border border-accent/40 bg-accent/10 px-3 py-2 text-xs text-ink"
          >
            <span className="font-display uppercase tracking-wide text-accent">
              Resume previous upload?
            </span>
            <span className="text-ink-muted">
              {`Found an interrupted upload of this file — ${
                (resumeOffer.bytes_uploaded / 1024 / 1024).toFixed(0)
              } of ${
                (resumeOffer.size_bytes / 1024 / 1024).toFixed(0)
              } MB already on the server (chunk ${resumeOffer.next_index} of ${resumeOffer.total_chunks}). Clicking Upload will continue from where it left off.`}
            </span>
            <button
              type="button"
              data-testid="coach-upload-modal-resume-discard"
              onClick={() => {
                // User explicitly wants to start over. Clearing the offer
                // alone wasn't enough — the localStorage entry survives a
                // modal close, so re-opening would re-trigger discovery
                // and offer the same resume again (PR #90 review P2).
                // Also wipe the stored entry so the next mount starts
                // fresh. The existing session on the server stays active
                // until the stale-session sweeper cleans it, or until the
                // user re-uploads, which will conflict and the server's
                // `cancel_conflicting_sessions` marks the old one
                // `replaced` and unlinks its raw.
                setResumeOffer(null);
                clearStoredSessionFor(match.id, slot);
              }}
              className="self-start rounded border border-rule px-2 py-0.5 text-[0.65rem] font-display uppercase tracking-wide text-ink-muted hover:text-ink"
            >
              Start over
            </button>
          </div>
        ) : null}

        {pending || uploadState.phase === "done" ? (
          <div data-testid="coach-upload-modal-progress" className="flex flex-col gap-1">
            <div className="h-2 w-full overflow-hidden rounded-full bg-bg-card">
              <div
                className="h-full bg-accent transition-all"
                style={{ width: `${percent ?? 0}%` }}
              />
            </div>
            <span className="font-mono text-[0.65rem] text-ink-muted">
              {phaseLabel ? `${phaseLabel} ` : ""}
              {percent ?? 0}%
            </span>
          </div>
        ) : null}

        {error ? (
          <p
            role="alert"
            data-testid="coach-upload-modal-error"
            className="rounded border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive"
          >
            {error}
          </p>
        ) : null}

        <div className="flex flex-col-reverse gap-2 border-t border-rule pt-3 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={cancelAndClose}
            className="rounded border border-rule bg-bg-card px-3 py-1.5 text-sm font-display uppercase tracking-wide text-ink hover:bg-surface"
          >
            {pending ? "Cancel upload" : "Cancel"}
          </button>
          <button
            type="submit"
            disabled={pending || !file}
            data-testid="coach-upload-modal-submit"
            className="rounded bg-accent px-3 py-1.5 text-sm font-display uppercase tracking-wide text-white hover:opacity-90 disabled:opacity-60"
          >
            {pending ? "Uploading…" : (resumeOffer ? "Resume upload" : "Upload")}
          </button>
        </div>
      </form>

      {confirmCancelOpen ? (
        <ConfirmDialog
          title="Cancel upload?"
          message={cancelConfirmMessage}
          confirmLabel="Cancel upload"
          cancelLabel="Keep uploading"
          danger
          data-testid="coach-upload-modal-confirm-cancel"
          onConfirm={() => {
            setConfirmCancelOpen(false);
            performCancelAndClose();
          }}
          onClose={() => setConfirmCancelOpen(false)}
        />
      ) : null}
    </div>
  );
}
